import {FC, useState, useRef, useEffect, KeyboardEvent, ClipboardEvent} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Typography,
  Avatar,
  TextField,
  Link,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {useVerifyOtpMutation, useSendOtpMutation} from "../../../app/services/otp";
import {getApiErrorMessage} from "../../../util/getApiErrorMessage";
import {useAppDispatch} from "../../../app/hooks";
import {setToken, toToken} from "../../../app/features/authSlice";
import {CustomerPortalConfig} from "../../../app/interfaces/dealerCustomer";
import {ALLOWED_PARENT_ORIGINS} from "../../../util/helpers";

const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

type StatusSeverity = "success" | "error" | "info";

export const OtpVerificationPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const sessionId = location.state?.sessionId || "";
  const identifier = location.state?.identifier || "";
  const maskedEmail = location.state?.maskedEmail;
  const maskedPhoneNumber = location.state?.maskedPhoneNumber;

  const [verifyOtp, {isLoading}] = useVerifyOtpMutation();
  const [sendOtp, {isLoading: isResending}] = useSendOtpMutation();

  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  // Inline status message shown inside the iframe instead of a toast. Seeded with the
  // "OTP sent" notice carried over from the request page.
  const [status, setStatus] = useState<{text: string; severity: StatusSeverity} | null>(
    location.state?.notice ? {text: location.state.notice, severity: "info"} : null
  );

  const [config, setConfig] = useState<CustomerPortalConfig | null>(
    location.state?.config || null
  );

  // Only trust a hostOrigin that is in the configured allowlist.
  useEffect(() => {
    const hostOriginParam = searchParams.get("hostOrigin");
    if (hostOriginParam && ALLOWED_PARENT_ORIGINS.includes(hostOriginParam)) {
      setHostOrigin(hostOriginParam);
    } else if (hostOriginParam) {
      console.error("Untrusted hostOrigin:", hostOriginParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (config) return;

    const handleMessage = (event: MessageEvent) => {
      // event.origin only gates which embedders may post config; the dealer is now supplied
      // directly by the host page via event.data.dealerGuid rather than derived from the origin.
      if (!ALLOWED_PARENT_ORIGINS.includes(event.origin)) {
        console.debug("Ignored config message from untrusted origin:", event.origin);
        return;
      }

      if (event.data?.type === "CUSTOMER_PORTAL_CONFIG") {
        setConfig({
          productId: event.data.productId,
          dealerGuid: event.data.dealerGuid,
          clientId: event.data.clientId,
          clientSecret: event.data.clientSecret,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin, config]);

  useEffect(() => {
    if (hostOrigin && !config) {
      window.parent.postMessage({type: "REQUEST_CUSTOMER_PORTAL_CONFIG"}, hostOrigin);
    }
  }, [hostOrigin, config]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.every(d => d !== "") && !isLoading) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Focus the first OTP input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const displayContact = maskedEmail || maskedPhoneNumber || identifier || "your registered contact";

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerify();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (!/^\d+$/.test(pastedData)) {
      setStatus({text: "Please paste numbers only", severity: "error"});
      return;
    }

    const pastedDigits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];

    pastedDigits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });

    setOtp(newOtp);

    const nextIndex = Math.min(pastedDigits.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      setStatus({text: "Please enter a complete 6-digit OTP", severity: "error"});
      return;
    }

    if (isLoading) return;

    if (!currentSessionId) {
      setStatus({text: "Session expired. Please request a new OTP.", severity: "error"});
      navigate("/otp-request");
      return;
    }

    const clientId = config?.clientId || "";
    const clientSecret = config?.clientSecret || "";
    const productId = config?.productId || "";

    try {
      const response = await verifyOtp({
        sessionId: currentSessionId,
        otpCode: otpValue,
        clientId,
        clientSecret,
        productId,
      }).unwrap();

      dispatch(setToken(toToken(response)));

      setStatus({text: "OTP verified successfully!", severity: "success"});

      if (hostOrigin) {
        window.parent.postMessage(
          {
            type: "LOGIN_SUCCESS",
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            expires_in: response.expires_in,
            token_type: response.token_type,
            scope: response.scope,
          },
          hostOrigin,
        );
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      setStatus({text: errorMessage || "Invalid OTP. Please try again.", severity: "error"});
    }
  };

  const handleResend = async () => {
    if (!identifier) {
      setStatus({text: "No identifier found. Please start over.", severity: "error"});
      navigate("/otp-request");
      return;
    }

    if (!config?.dealerGuid) {
      setStatus({text: "The portal is still loading. Please try again in a moment.", severity: "error"});
      return;
    }

    try {
      const isEmailInput = isEmail(identifier);

      const response = await sendOtp({
        email: isEmailInput ? identifier : "",
        phoneNumber: isEmailInput ? undefined : identifier,
        dealerGuid: config.dealerGuid,
        productId: config?.productId || "",
      }).unwrap();

      setCurrentSessionId(response.sessionId || "");

      setStatus({
        text: response.message || "A new one-time passcode has been sent.",
        severity: "info",
      });
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      setStatus({
        text: errorMessage || "Failed to resend OTP. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "background.paper",
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: "100%",
        }}>
        <Avatar sx={{bgcolor: "primary.main", mb: 2}}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component="h1" variant="h5" sx={{mb: 1}}>
          Enter Passcode
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{mb: 1}}>
          Enter the 6-digit code sent to
        </Typography>

        <Typography
          variant="body2"
          color="primary"
          fontWeight="medium"
          align="center"
          sx={{mb: 3}}>
          {displayContact}
        </Typography>

        {status && (
          <Alert
            severity={status.severity}
            onClose={() => setStatus(null)}
            sx={{width: "100%", mb: 2}}>
            {status.text}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 3,
            justifyContent: "center",
          }}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={el => (inputRefs.current[index] = el)}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                },
              }}
              sx={{
                width: "50px",
                "& input": {
                  padding: "12px",
                },
              }}
              variant="outlined"
            />
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          sx={{mb: 2, py: 1.2}}
          disabled={isLoading}>
          {isLoading ? "Verifying..." : "Verify & Sign In"}
        </Button>

        <Box sx={{textAlign: "center"}}>
          <Typography variant="body2" color="text.secondary">
            Can't find your code? Check your spam/junk folder or search for our email.{" "}
            <Link
              component="button"
              variant="body2"
              onClick={handleResend}
              disabled={isResending}
              sx={{
                cursor: isResending ? "not-allowed" : "pointer",
                textDecoration: "underline",
              }}>
              {isResending ? "Resending..." : "Resend Code"}
            </Link>
          </Typography>
        </Box>

        <Box sx={{mt: 3, textAlign: "center"}}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate("/otp-request")}
            sx={{cursor: "pointer"}}>
            ← Try a different Email or Phone
          </Link>
        </Box>
      </Box>
    </Container>
  );
};
